// =====================================================================
// TARPI — Catalog, Quote Cart and Product Detail
// =====================================================================

const { useState, useEffect, useMemo, useRef } = React;

// ---------- ICONS ----------
const I = {
  arrow: (p) => (
    <svg className={p.className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <path d="M5 12h14M13 6l6 6-6 6"/>
    </svg>
  ),
  plus: (p) => (
    <svg className={p.className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M12 5v14M5 12h14"/>
    </svg>
  ),
  check: (p) => (
    <svg className={p.className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6 9 17l-5-5"/>
    </svg>
  ),
  close: (p) => (
    <svg className={p.className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <path d="M18 6 6 18M6 6l12 12"/>
    </svg>
  ),
  cart: (p) => (
    <svg className={p.className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 4h2l2.5 12h12l2-9H7"/>
      <circle cx="9" cy="20" r="1.4"/>
      <circle cx="18" cy="20" r="1.4"/>
    </svg>
  ),
  trash: (p) => (
    <svg className={p.className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 7h16M10 11v6M14 11v6M5 7l1 13a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-13M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/>
    </svg>
  ),
};

// ---------- BAG ILLUSTRATION ----------
// Stylized feed bag — placeholder until photo renders provided.
function FeedBag({ line, name, category, small }) {
  const config = {
    rumax: {
      bg: "#0E2A1E",
      accent: "#2F8E47",
      accent2: "#5DC97E",
      stripe: "#163E2A",
      patternColor: "#1F5C36"
    },
    ovomax: {
      bg: "#F4ECD8",
      accent: "#D9A441",
      accent2: "#8B5E1B",
      stripe: "#EFE0BA",
      patternColor: "#C49233"
    },
    suimax: {
      bg: "#2A0E10",
      accent: "#A33038",
      accent2: "#D9707A",
      stripe: "#4A171B",
      patternColor: "#8B2635"
    }
  }[line];

  if (!config) return null;
  const fg = line === "ovomax" ? "#2B221A" : "#FFFFFF";
  const subFg = line === "ovomax" ? "rgba(43,34,26,0.65)" : "rgba(255,255,255,0.7)";

  return (
    <svg viewBox="0 0 200 260" xmlns="http://www.w3.org/2000/svg" style={{filter: small ? "none" : "drop-shadow(0 30px 40px rgba(0,0,0,0.18))"}}>
      <defs>
        <linearGradient id={`bag-grad-${line}-${name}`} x1="0" x2="1" y1="0" y2="1">
          <stop offset="0" stopColor={config.bg} />
          <stop offset="1" stopColor={config.stripe} />
        </linearGradient>
        <clipPath id={`bag-clip-${line}-${name}`}>
          <path d="M30 30 Q30 22 38 22 L162 22 Q170 22 170 30 L172 240 Q172 250 162 250 L38 250 Q28 250 28 240 Z"/>
        </clipPath>
      </defs>
      {/* Top fold/heat seal */}
      <path d="M30 30 Q30 18 100 14 Q170 18 170 30 L172 38 L28 38 Z" fill={config.accent} opacity={line === "ovomax" ? "0.85" : "1"}/>
      {/* Bag body */}
      <path d="M30 30 Q30 22 38 22 L162 22 Q170 22 170 30 L172 240 Q172 250 162 250 L38 250 Q28 250 28 240 Z"
            fill={`url(#bag-grad-${line}-${name})`}/>
      {/* Subtle texture stripes */}
      <g clipPath={`url(#bag-clip-${line}-${name})`} opacity="0.18">
        {Array.from({length: 14}).map((_, i) => (
          <rect key={i} x="0" y={20 + i*18} width="200" height="1" fill={config.patternColor}/>
        ))}
      </g>
      {/* Accent band */}
      <rect x="28" y="58" width="144" height="2" fill={config.accent} opacity="0.9"/>
      <rect x="28" y="218" width="144" height="2" fill={config.accent} opacity="0.6"/>
      {/* Line logo area */}
      <text x="100" y="92" textAnchor="middle" fontFamily="Bricolage Grotesque, sans-serif" fontWeight="700" fontSize="28" letterSpacing="-0.02em" fill={fg}>
        {line.toUpperCase()}
      </text>
      {/* Dots decorative — recalls RUMAX logo */}
      <g opacity="0.5">
        <circle cx="153" cy="86" r="2.2" fill={config.accent2}/>
        <circle cx="159" cy="92" r="1.4" fill={config.accent2}/>
        <circle cx="163" cy="84" r="1" fill={config.accent2}/>
        <circle cx="157" cy="82" r="0.8" fill={config.accent2}/>
      </g>
      {/* Product name */}
      <text x="100" y="125" textAnchor="middle" fontFamily="Plus Jakarta Sans, sans-serif" fontWeight="600" fontSize="12" letterSpacing="0.06em" fill={fg}>
        {name.toUpperCase()}
      </text>
      {/* Category */}
      <text x="100" y="142" textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontWeight="500" fontSize="7" letterSpacing="0.18em" fill={subFg}>
        {(category || "").toUpperCase()}
      </text>
      {/* Eco/tech mark */}
      <g transform="translate(100 175)">
        <circle r="22" fill="none" stroke={config.accent} strokeWidth="0.8" opacity="0.6"/>
        <circle r="14" fill="none" stroke={config.accent} strokeWidth="0.5" opacity="0.4"/>
        <path d="M-6 0 L-2 4 L6 -4" stroke={config.accent} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
      </g>
      {/* Bottom info */}
      <text x="100" y="218" textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontWeight="500" fontSize="6" letterSpacing="0.2em" fill={subFg}>
        TARPI · NUTRICIÓN ANIMAL
      </text>
      {/* Weight tag */}
      <rect x="60" y="225" width="80" height="14" fill={config.accent} rx="2"/>
      <text x="100" y="235" textAnchor="middle" fontFamily="Plus Jakarta Sans" fontWeight="700" fontSize="8" letterSpacing="0.1em" fill={line === "ovomax" ? "#2B221A" : "white"}>
        25 kg
      </text>
    </svg>
  );
}

// ---------- PRODUCT CARD ----------
function ProductCard({ product, onOpen, onAdd, inCart }) {
  const hasImg = !!product.imageThumb;
  return (
    <article className={`product-card line-${product.line}${hasImg ? ' has-photo' : ''}`} onClick={() => onOpen(product)}>
      <div className="product-line">
        {product.line.toUpperCase()} · {product.categoryLabel}
      </div>
      <div className="product-bag">
        {hasImg ? (
          <div className="product-photo">
            <img src={product.imageThumb} alt={product.name} loading="lazy" />
            {/* Overlay con nombre y categoría — igual que el FeedBag SVG */}
            <div className="product-photo-label">
              <span className="product-photo-name">{product.name}</span>
              <span className="product-photo-cat">{product.categoryLabel}</span>
            </div>
          </div>
        ) : (
          <FeedBag line={product.line} name={product.name} category={product.categoryLabel} />
        )}
      </div>
      <div className="product-info">
        <h3 className="product-name">{product.name}</h3>
        <p className="product-cat">{product.species} · {product.stage}</p>
        <div className="product-meta">
          <span className="product-tag">{product.highlights[0]}</span>
          <button
            className={`product-add ${inCart ? "added" : ""}`}
            onClick={(e) => { e.stopPropagation(); onAdd(product); }}
            aria-label="Agregar a cotización"
            title={inCart ? "Agregado" : "Agregar a cotización"}
          >
            {inCart ? <I.check className="" /> : <I.plus className="" />}
          </button>
        </div>
      </div>
    </article>
  );
}

// ---------- PRODUCT DETAIL DRAWER ----------
function ProductDetail({ product, onClose, onAdd, inCart }) {
  useEffect(() => {
    if (product) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [product]);

  return (
    <React.Fragment>
      <div className={`product-detail-overlay ${product ? "open" : ""}`} onClick={onClose} />
      <aside className={`product-detail ${product ? "open" : ""}`}>
        {product && (
          <React.Fragment>
            <div className="product-detail-header">
              <div>
                <span className="line-name-small">{product.line.toUpperCase()} · {product.categoryLabel}</span>
              </div>
              <button className="product-detail-close" onClick={onClose} aria-label="Cerrar">
                <I.close className="" />
              </button>
            </div>
            <div className="product-detail-content">
              <div className="product-detail-hero">
                <div className="bag-large">
                  {product.image ? (
                    <div className="product-detail-real-img">
                      <img src={product.image} alt={product.name} loading="eager" />
                      <div className="product-detail-img-label">
                        <span>{product.name}</span>
                        <span style={{fontSize:11,opacity:.7}}>{product.categoryLabel}</span>
                      </div>
                    </div>
                  ) : (
                    <FeedBag line={product.line} name={product.name} category={product.categoryLabel} />
                  )}
                </div>
                <div>
                  <span className="line-name-small">{window.TARPI_LINES[product.line].name}</span>
                  <h2>{product.name}</h2>
                  <p className="product-detail-cat">{product.species} · {product.stage}</p>
                  <p style={{margin: "0 0 24px", color: "var(--ink-700)", lineHeight: 1.55}}>{product.description}</p>
                  <div style={{display: "flex", flexWrap: "wrap", gap: 8}}>
                    {product.highlights.map((h, i) => (
                      <span key={i} style={{
                        fontSize: 12,
                        padding: "6px 12px",
                        background: "var(--bg-cream)",
                        borderRadius: 999,
                        color: "var(--ink-700)",
                        fontFamily: "var(--font-mono)",
                        letterSpacing: "0.04em"
                      }}>{h}</span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="product-detail-section">
                <h4>Niveles de garantía</h4>
                <div className="spec-table">
                  {product.specs.map((s, i) => (
                    <div className="spec-cell" key={i}>
                      <div className="spec-label">{s.label}</div>
                      <div className="spec-value">{s.value}<span className="spec-unit"> {s.unit}</span></div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="product-detail-section">
                <h4>Uso del producto</h4>
                <p>{product.use}</p>
              </div>

              <div className="product-detail-section">
                <h4>Validez</h4>
                <p>{product.validity}</p>
                <h4 style={{marginTop: 20}}>Restricciones y recomendaciones</h4>
                <p>{product.restrictions}</p>
                <h4 style={{marginTop: 20}}>Conservación</h4>
                <p>Conservar en la bolsa original, en lugar seco y ventilado, fuera de la incidencia directa de los rayos solares.</p>
              </div>
            </div>
            <div className="product-detail-cta">
              <button className="btn btn-ghost" onClick={onClose}>Seguir explorando</button>
              <button className={`btn btn-primary`} onClick={() => onAdd(product)}>
                {inCart ? <React.Fragment><I.check className="arrow"/> Agregado a cotización</React.Fragment>
                        : <React.Fragment>Agregar a cotización <I.plus className="arrow"/></React.Fragment>}
              </button>
            </div>
          </React.Fragment>
        )}
      </aside>
    </React.Fragment>
  );
}

// ---------- FEATURED RAIL (above catalog) ----------
function Featured({ onOpen }) {
  // Pick 4 hero products — one per category essentially
  const featuredIds = ["rumax-iniciador-prime", "rumax-pe25", "ovomax-postura", "ovomax-pp30"];
  const featured = featuredIds
    .map(id => window.TARPI_PRODUCTS.find(p => p.id === id))
    .filter(Boolean);

  return (
    <section className="featured" aria-label="Productos destacados">
      <div className="featured-head">
        <div>
          <span className="eyebrow">04 / Productos destacados</span>
          <h3 style={{marginTop: 18}}>
            Los más solicitados <em className="serif-italic gradient-text">esta temporada.</em>
          </h3>
        </div>
        <p>Una selección curada por nuestro equipo técnico. Tocá una bolsa para ver niveles de garantía completos.</p>
      </div>
      <div className="featured-rail">
        {featured.map((p, i) => (
          <article
            key={p.id}
            className={`featured-card ${p.line}`}
            onClick={() => onOpen(p)}
          >
            <div className="featured-visual">
              {p.imageThumb
                ? <img src={p.imageThumb} alt={p.name} className="featured-photo" loading="lazy" width="300" height="300" />
                : <div className="featured-bag"><FeedBag line={p.line} name={p.name} category={p.categoryLabel} /></div>
              }
            </div>
            <div className="featured-info">
              <span className="featured-line">{p.line.toUpperCase()} · {p.categoryLabel}</span>
              <h4 className="featured-name">{p.name}</h4>
              <div className="featured-meta">
                <span>{p.species}</span>
                <span className="featured-arrow">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                    <path d="M5 12h14M13 6l6 6-6 6"/>
                  </svg>
                </span>
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

// ---------- CATALOG SECTION ----------
function Catalog({ onAdd, cart, onOpenProduct }) {
  const [line, setLine] = useState("all"); // all | rumax | ovomax | suimax
  const [category, setCategory] = useState("all");

  const filtered = useMemo(() => {
    return window.TARPI_PRODUCTS.filter(p => {
      if (line !== "all" && p.line !== line) return false;
      if (category !== "all" && p.category !== category) return false;
      return true;
    });
  }, [line, category]);

  const categoryCounts = useMemo(() => {
    const counts = { all: 0 };
    window.TARPI_PRODUCTS.forEach(p => {
      if (line !== "all" && p.line !== line) return;
      counts.all += 1;
      counts[p.category] = (counts[p.category] || 0) + 1;
    });
    return counts;
  }, [line]);

  const availableCategories = useMemo(() => {
    if (line === "all") return Object.keys(window.TARPI_CATEGORIES);
    return window.TARPI_LINES[line].categories;
  }, [line]);

  useEffect(() => { setCategory("all"); }, [line]);

  // listen for external line filter events (from line cards / hero buttons)
  useEffect(() => {
    const handler = (e) => setLine(e.detail.line);
    window.addEventListener("tarpi:set-catalog-line", handler);
    return () => window.removeEventListener("tarpi:set-catalog-line", handler);
  }, []);

  const inCartIds = new Set(cart.map(c => c.id));

  return (
    <section className="catalog" id="catalogo">
      <div className="section-head">
        <div>
          <span className="eyebrow">05 / Catálogo</span>
        </div>
        <div>
          <h2 className="display display-md">Todo el portfolio. Filtrá, explorá, cotizá.</h2>
          <p style={{marginTop: 20}}>{window.TARPI_PRODUCTS.length} productos disponibles entre balanceados, proteinados, concentrados, minerales y núcleos vitamínicos. Hacé clic en cualquiera para ver los niveles de garantía completos.</p>
        </div>
      </div>

      <div className="catalog-toolbar">
        <div className="catalog-tabs">
          <button className={`catalog-tab ${line === "all" ? "active" : ""}`} onClick={() => setLine("all")}>
            <span className="tab-label-full">Todas las líneas</span>
            <span className="tab-label-short">Todas</span>
          </button>
          <button className={`catalog-tab ${line === "rumax" ? "active" : ""}`} onClick={() => setLine("rumax")}>RUMAX</button>
          <button className={`catalog-tab ${line === "ovomax" ? "active" : ""}`} onClick={() => setLine("ovomax")}>OVOMAX</button>
          <button className={`catalog-tab ${line === "suimax" ? "active" : ""}`} onClick={() => setLine("suimax")}>SUIMAX</button>
        </div>
        <div className="catalog-filters">
          <button className={`filter-chip ${category === "all" ? "active" : ""}`} onClick={() => setCategory("all")}>
            Todos <span className="count">{categoryCounts.all}</span>
          </button>
          {availableCategories.map(catKey => (
            <button
              key={catKey}
              className={`filter-chip ${category === catKey ? "active" : ""}`}
              onClick={() => setCategory(catKey)}
            >
              {window.TARPI_CATEGORIES[catKey]}
              <span className="count">{categoryCounts[catKey] || 0}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="catalog-grid">
        {filtered.map(p => (
          <ProductCard
            key={p.id}
            product={p}
            onOpen={onOpenProduct}
            onAdd={onAdd}
            inCart={inCartIds.has(p.id)}
          />
        ))}
      </div>
    </section>
  );
}

// ---------- QUOTE CART (multi-step wizard) ----------
function QuoteCart({ open, onClose, cart, setCart }) {
  const [step, setStep] = useState(1); // 1 = products, 2 = data
  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "",
    country: "Paraguay", department: "", city: "", neighborhood: "",
    message: ""
  });
  const [submitted, setSubmitted] = useState(false);

  // Paraguay departments
  const departments = [
    "Asunción (Capital)", "Central", "Alto Paraná", "Itapúa", "Caaguazú",
    "Concepción", "San Pedro", "Cordillera", "Guairá", "Caazapá",
    "Misiones", "Paraguarí", "Alto Paraguay", "Ñeembucú", "Amambay",
    "Canindeyú", "Presidente Hayes", "Boquerón"
  ];

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      // Reset to step 1 when re-opening
      setStep(1);
      setSubmitted(false);
      return () => { document.body.style.overflow = ""; };
    }
  }, [open]);

  // If cart empty, force step 1
  useEffect(() => {
    if (cart.length === 0) setStep(1);
  }, [cart.length]);

  const updateQty = (id, qty) => {
    setCart(c => c.map(i => i.id === id ? {...i, qty: Math.max(1, qty)} : i));
  };
  const remove = (id) => setCart(c => c.filter(i => i.id !== id));

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setCart([]);
      setStep(1);
      onClose();
    }, 4000);
  };

  const totalUnits = cart.reduce((acc, i) => acc + (i.qty || 1), 0);
  const lineCounts = cart.reduce((acc, i) => { acc[i.line] = (acc[i.line] || 0) + 1; return acc; }, {});

  return (
    <React.Fragment>
      <div className={`cart-overlay ${open ? "open" : ""}`} onClick={onClose} />
      <div className={`cart ${open ? "open" : ""}`} role="dialog" aria-label="Solicitud de cotización">
        <div className="cart-header">
          <div>
            <h3>Solicitud de cotización</h3>
            {!submitted && cart.length > 0 && (
              <div className="cart-stepper">
                <div className={`step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'done' : ''}`}>
                  <span className="step-num">{step > 1 ? "✓" : "1"}</span>
                  <span className="step-label">Productos</span>
                </div>
                <div className="step-divider"></div>
                <div className={`step ${step >= 2 ? 'active' : ''}`}>
                  <span className="step-num">2</span>
                  <span className="step-label">Tus datos</span>
                </div>
              </div>
            )}
          </div>
          <button className="product-detail-close" onClick={onClose} aria-label="Cerrar">
            <I.close className="" />
          </button>
        </div>

        {submitted ? (
          <div style={{flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 32, textAlign: "center"}}>
            <div className="cart-success-circle">
              <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
            </div>
            <h3 style={{fontFamily: "var(--font-display)", fontSize: 28, margin: "0 0 12px", color: "var(--ink-900)"}}>¡Solicitud enviada!</h3>
            <p style={{color: "var(--ink-600)", maxWidth: "32ch"}}>Un representante técnico de TARPI te contactará a <strong style={{color: "var(--ink-900)"}}>{form.email}</strong> en menos de 24 horas para confirmar tu cotización.</p>
          </div>
        ) : cart.length === 0 ? (
          <div className="cart-empty">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" style={{width: 64, height: 64, margin: "0 auto 20px", opacity: 0.4}}>
              <path d="M3 4h2l2.5 12h12l2-9H7"/>
              <circle cx="9" cy="20" r="1.4"/>
              <circle cx="18" cy="20" r="1.4"/>
            </svg>
            <p style={{margin: "0 0 8px", fontSize: 17, color: "var(--ink-700)"}}>Tu cotización está vacía</p>
            <p style={{margin: 0, fontSize: 14}}>Agregá productos desde el catálogo para empezar.</p>
          </div>
        ) : step === 1 ? (
          // STEP 1: PRODUCTS
          <React.Fragment>
            <div className="cart-list">
              <p className="cart-step-help">
                Revisá los productos y ajustá cantidades. Cuando estés listo, continuá con tus datos.
              </p>
              {cart.map(item => (
                <div className="cart-item" key={item.id}>
                  <div className="cart-item-bag">
                    {item.imageThumb
                      ? <img src={item.imageThumb} alt={item.name} className="cart-item-photo" loading="lazy" />
                      : <FeedBag line={item.line} name={item.name} category={item.categoryLabel} small />
                    }
                  </div>
                  <div>
                    <h5 className="cart-item-name">{item.name}</h5>
                    <span className="cart-item-line">{(item.line || "").toUpperCase()} · {item.categoryLabel || ""}</span>
                    <div className="qty-controls">
                      <button onClick={() => updateQty(item.id, item.qty - 1)} aria-label="Disminuir">−</button>
                      <input type="number" min="1" value={item.qty} onChange={(e) => updateQty(item.id, parseInt(e.target.value) || 1)} />
                      <button onClick={() => updateQty(item.id, item.qty + 1)} aria-label="Aumentar">+</button>
                      <span className="qty-unit">bolsas / sacos</span>
                    </div>
                  </div>
                  <button className="cart-item-remove" onClick={() => remove(item.id)} aria-label="Eliminar">
                    <I.trash className="" />
                  </button>
                </div>
              ))}
            </div>

            <div className="cart-summary" style={{padding: "14px 28px 0", flexShrink: 0}}>
              <div>
                <span className="cart-summary-label">Resumen</span>
                <span className="cart-summary-value">
                  {cart.length} producto{cart.length !== 1 ? 's' : ''} · {totalUnits} unidad{totalUnits !== 1 ? 'es' : ''}
                </span>
              </div>
              <div className="cart-summary-tags">
                {lineCounts.rumax && <span className="cart-line-tag rumax">RUMAX · {lineCounts.rumax}</span>}
                {lineCounts.ovomax && <span className="cart-line-tag ovomax">OVOMAX · {lineCounts.ovomax}</span>}
                {lineCounts.suimax && <span className="cart-line-tag suimax">SUIMAX · {lineCounts.suimax}</span>}
              </div>
            </div>

            <div className="cart-foot">
              <button className="cart-submit" onClick={() => setStep(2)}>
                Continuar con mis datos
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
              </button>
              <p style={{fontSize: 11, color: "var(--ink-500)", textAlign: "center", margin: "12px 0 0", lineHeight: 1.4}}>
                Te respondemos en menos de 24h hábiles. Datos confidenciales — no se comparten con terceros.
              </p>
            </div>
          </React.Fragment>
        ) : (
          // STEP 2: DATA
          <form className="cart-form-wizard" onSubmit={handleSubmit}>
            <div className="cart-list cart-list-summary">
              <button type="button" className="cart-back-btn" onClick={() => setStep(1)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M19 12H5M11 18l-6-6 6-6"/></svg>
                Volver a productos
              </button>
              <div className="cart-recap">
                <strong>{cart.length} producto{cart.length !== 1 ? 's' : ''}</strong>
                <span>en tu cotización · {totalUnits} unidades</span>
              </div>

              <h4 className="cart-form-title">Información de contacto</h4>
              <div className="form-row">
                <div className="form-field">
                  <label>Nombre *</label>
                  <input type="text" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Juan Pérez"/>
                </div>
                <div className="form-field">
                  <label>Empresa / Establecimiento</label>
                  <input type="text" value={form.company} onChange={e => setForm({...form, company: e.target.value})} placeholder="Estancia La Esperanza"/>
                </div>
              </div>
              <div className="form-row">
                <div className="form-field">
                  <label>Email *</label>
                  <input type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="juan@empresa.com"/>
                </div>
                <div className="form-field">
                  <label>Teléfono *</label>
                  <input type="tel" required value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="+595 9XX XXX XXX"/>
                </div>
              </div>

              <h4 className="cart-form-title">Ubicación de entrega</h4>
              <div className="form-row">
                <div className="form-field">
                  <label>País *</label>
                  <select required value={form.country} onChange={e => setForm({...form, country: e.target.value, department: e.target.value !== "Paraguay" ? "" : form.department})}>
                    <option>Paraguay</option>
                    <option>Argentina</option>
                    <option>Brasil</option>
                    <option>Bolivia</option>
                    <option>Uruguay</option>
                    <option>Otro</option>
                  </select>
                </div>
                <div className="form-field">
                  <label>Departamento {form.country === "Paraguay" ? "*" : ""}</label>
                  {form.country === "Paraguay" ? (
                    <select required value={form.department} onChange={e => setForm({...form, department: e.target.value})}>
                      <option value="">Seleccionar…</option>
                      {departments.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  ) : (
                    <input type="text" value={form.department} onChange={e => setForm({...form, department: e.target.value})} placeholder="Provincia / Estado"/>
                  )}
                </div>
              </div>
              <div className="form-row">
                <div className="form-field">
                  <label>Ciudad / Localidad *</label>
                  <input type="text" required value={form.city} onChange={e => setForm({...form, city: e.target.value})} placeholder="San Lorenzo"/>
                </div>
                <div className="form-field">
                  <label>Barrio / Compañía</label>
                  <input type="text" value={form.neighborhood} onChange={e => setForm({...form, neighborhood: e.target.value})} placeholder="Villa Concordia"/>
                </div>
              </div>

              <h4 className="cart-form-title">Mensaje adicional</h4>
              <div className="form-row single">
                <div className="form-field">
                  <label>Notas (opcional)</label>
                  <textarea rows="3" value={form.message} onChange={e => setForm({...form, message: e.target.value})} placeholder="Cantidad estimada de animales, plazo deseado, condiciones especiales de entrega…"/>
                </div>
              </div>
            </div>

            <div className="cart-foot">
              <button type="submit" className="cart-submit">
                Enviar solicitud
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
              </button>
              <p style={{fontSize: 11, color: "var(--ink-500)", textAlign: "center", margin: "12px 0 0", lineHeight: 1.4}}>
                Te respondemos en menos de 24h hábiles. Datos confidenciales — no se comparten con terceros.
              </p>
            </div>
          </form>
        )}
      </div>
    </React.Fragment>
  );
}

// Export to window so app.jsx can use
Object.assign(window, { FeedBag, ProductCard, ProductDetail, Catalog, QuoteCart, Featured, Icons: I });
